import { Notification, NotificationPreferences, NotificationStats } from '../types/notifications'

class NotificationService {
  private readonly STORAGE_KEY = 'gomtb_notifications'
  private readonly PREFERENCES_KEY = 'gomtb_notification_preferences'
  private readonly MAX_NOTIFICATIONS = 100
  
  private listeners: Set<() => void> = new Set()

  /**
   * Get all notifications for the current user
   */
  getNotifications(): Notification[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) return []
      
      const notifications = JSON.parse(stored) as Notification[]
      
      // Convert timestamp strings back to Date objects
      return notifications.map(notification => ({
        ...notification,
        timestamp: new Date(notification.timestamp),
        expiresAt: notification.expiresAt ? new Date(notification.expiresAt) : undefined
      })).filter(notification => {
        // Remove expired notifications
        if (notification.expiresAt && notification.expiresAt < new Date()) {
          return false
        }
        return true
      }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    } catch (error) {
      console.error('Error loading notifications:', error)
      return []
    }
  }

  /**
   * Add a new notification
   */
  addNotification(notification: Omit<Notification, 'id' | 'timestamp' | 'read'>): Notification {
    const newNotification: Notification = {
      ...notification,
      id: this.generateId(),
      timestamp: new Date(),
      read: false
    }

    const notifications = this.getNotifications()
    notifications.unshift(newNotification)

    // Keep only the most recent notifications
    const trimmedNotifications = notifications.slice(0, this.MAX_NOTIFICATIONS)
    
    this.saveNotifications(trimmedNotifications)
    this.notifyListeners()
    
    return newNotification
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    const notifications = this.getNotifications()
    const notification = notifications.find(n => n.id === notificationId)
    
    if (notification && !notification.read) {
      notification.read = true
      this.saveNotifications(notifications)
      this.notifyListeners()
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    const notifications = this.getNotifications()
    let hasChanges = false
    
    notifications.forEach(notification => {
      if (!notification.read) {
        notification.read = true
        hasChanges = true
      }
    })
    
    if (hasChanges) {
      this.saveNotifications(notifications)
      this.notifyListeners()
    }
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId: string): void {
    const notifications = this.getNotifications()
    const filteredNotifications = notifications.filter(n => n.id !== notificationId)
    
    if (filteredNotifications.length !== notifications.length) {
      this.saveNotifications(filteredNotifications)
      this.notifyListeners()
    }
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    localStorage.removeItem(this.STORAGE_KEY)
    this.notifyListeners()
  }

  /**
   * Get unread notifications count
   */
  getUnreadCount(): number {
    return this.getNotifications().filter(n => !n.read).length
  }

  /**
   * Get notification statistics
   */
  getStats(): NotificationStats {
    const notifications = this.getNotifications()
    
    const stats: NotificationStats = {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
      byType: {
        info: 0,
        success: 0,
        warning: 0,
        error: 0,
        event: 0,
        payment: 0,
        system: 0
      },
      byCategory: {
        general: 0,
        events: 0,
        payments: 0,
        system: 0,
        coordinator: 0,
        registration: 0
      },
      byPriority: {
        low: 0,
        medium: 0,
        high: 0,
        urgent: 0
      }
    }

    notifications.forEach(notification => {
      stats.byType[notification.type]++
      stats.byCategory[notification.category]++
      stats.byPriority[notification.priority]++
    })

    return stats
  }

  /**
   * Get notification preferences
   */
  getPreferences(): NotificationPreferences {
    try {
      const stored = localStorage.getItem(this.PREFERENCES_KEY)
      if (!stored) {
        return this.getDefaultPreferences()
      }
      return JSON.parse(stored)
    } catch (error) {
      console.error('Error loading notification preferences:', error)
      return this.getDefaultPreferences()
    }
  }

  /**
   * Update notification preferences
   */
  updatePreferences(preferences: NotificationPreferences): void {
    localStorage.setItem(this.PREFERENCES_KEY, JSON.stringify(preferences))
    this.notifyListeners()
  }

  /**
   * Subscribe to notification changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }



  private saveNotifications(notifications: Notification[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications))
    } catch (error) {
      console.error('Error saving notifications:', error)
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener())
  }

  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private getDefaultPreferences(): NotificationPreferences {
    return {
      emailNotifications: true,
      pushNotifications: true,
      categories: {
        general: true,
        events: true,
        payments: true,
        system: true,
        coordinator: true,
        registration: true
      },
      priority: {
        low: true,
        medium: true,
        high: true,
        urgent: true
      }
    }
  }
}

export const notificationService = new NotificationService() 